import { AuthMeUserResponse } from "@/types/auth-me.types";

/**
 * `/user/me` returns permission groups, not a flat `permissionCodes` array.
 * JWT/`userData` from elit-tools has `permissionCodes`, but the me endpoint does not.
 */
export const hasActivePermissions = (user: AuthMeUserResponse | null | undefined): boolean => {
    if (Array.isArray(user?.permissionCodes) && user.permissionCodes.length > 0) {
        return true;
    }

    if (!Array.isArray(user?.permissionGroups)) {
        return false;
    }

    return user.permissionGroups.some((group) => Array.isArray(group.permissions) && group.permissions.length > 0);
};

/**
 * Permission codes a user gets in their JWT, worked out from an auth-svc user response
 * (`/user/me`, `/user/find/id/:id`): codes of all permission groups plus extra permissions, minus blocked ones.
 * Same merge as auth-svc's PermissionService.mergePermissions.
 */
export const getEffectivePermissionCodes = (user: AuthMeUserResponse | null | undefined): string[] => {
    if (Array.isArray(user?.permissionCodes)) {
        return user.permissionCodes;
    }

    const groupCodes = (Array.isArray(user?.permissionGroups) ? user.permissionGroups : []).flatMap((group) =>
        group && typeof group === "object" && Array.isArray(group.permissions) ? group.permissions : []
    );
    const blocked = new Set(user?.blockedPermissions ?? []);

    return [...new Set([...groupCodes, ...(user?.extraPermissions ?? [])])].filter((code) => !blocked.has(code));
};
